// ===== 存储加密（AES-256-GCM + PBKDF2）=====
// 需求：工作台上传云端的敏感数据（客户/供应商/商品/销售单）加密存储与传输
// 方案：Web Crypto API —— PBKDF2 派生密钥 + AES-256-GCM 加密
//  - 口令不落盘，只存 salt + 校验哈希（verifier）
//  - 会话内解锁后密钥缓存在内存 + sessionStorage（关浏览器即失效）
//  - 加密负载格式：enc:v1:<base64 iv>:<base64 ciphertext>

const SETTINGS_KEY = 'changfa_encryption';
const SESSION_KEY = 'changfa_enc_session_key';
const LOCKOUT_KEY = 'changfa_enc_lockout_v1';
/** 新启用/重设口令使用的 PBKDF2 迭代次数（OWASP 2023 建议 ≥600k） */
const ITERATIONS = 600_000;
/** 历史设置无 iterations 字段时按 100k 解锁，解锁成功后自动升级 */
const LEGACY_ITERATIONS = 100_000;
const MIN_PASS_LENGTH = 8;
/** 解锁失败次数上限与锁定时长（防暴力枚举） */
const MAX_UNLOCK_FAILS = 5;
const LOCKOUT_MS = 5 * 60_000;
const ENC_PREFIX = 'enc:v1:';

interface EncSettings {
  enabled: boolean;
  salt: string;      // base64
  verifier: string;  // hex（PBKDF2 派生位校验口令）
  /** PBKDF2 迭代次数；旧设置无此字段 = 100k，解锁成功后自动升级 */
  iterations?: number;
  createdAt: string;
  updatedAt: string;
}

interface LockoutState {
  fails: number;
  until: number; // 毫秒时间戳，0 = 未锁定
}

let cachedKey: CryptoKey | null = null;

interface EncryptionStorageMigrator {
  reencryptAll(passphrase: string): Promise<void>;
  decryptAllToPlain(): Promise<void>;
}

let storageMigrator: EncryptionStorageMigrator | null = null;

export function registerEncryptionStorageMigrator(migrator: EncryptionStorageMigrator): void {
  storageMigrator = migrator;
}

function requireStorageMigrator(): EncryptionStorageMigrator {
  if (!storageMigrator) throw new Error('加密存储模块尚未初始化，请刷新页面后重试');
  return storageMigrator;
}

function bufToBase64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}
function base64ToBuf(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}
function bufToHex(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function subtle(): SubtleCrypto {
  if (!crypto?.subtle) throw new Error('当前环境不支持 Web Crypto（需 HTTPS 或 localhost）');
  return crypto.subtle;
}

export function isSecureContext(): boolean {
  return typeof window !== 'undefined' && window.isSecureContext;
}

// ===== 设置读写 =====
export function getEncSettings(): EncSettings | null {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as EncSettings;
    return s.enabled ? s : null;
  } catch {
    return null;
  }
}

export function isEncryptionEnabled(): boolean {
  return !!getEncSettings();
}

export function hasSessionKey(): boolean {
  try {
    return !!sessionStorage.getItem(SESSION_KEY);
  } catch {
    return !!cachedKey;
  }
}

// ===== 密钥派生与解锁 =====
async function deriveBits(passphrase: string, salt: Uint8Array, iterations: number = ITERATIONS): Promise<ArrayBuffer> {
  const enc = new TextEncoder();
  const material = await subtle().importKey('raw', enc.encode(passphrase), 'PBKDF2', false, ['deriveBits']);
  return subtle().deriveBits(
    { name: 'PBKDF2', salt: salt.buffer as ArrayBuffer, iterations, hash: 'SHA-256' },
    material,
    256
  );
}

async function importAesKey(bits: ArrayBuffer): Promise<CryptoKey> {
  return subtle().importKey('raw', bits, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

// ===== 解锁限速（防口令暴力枚举） =====
function getLockoutState(): LockoutState {
  try {
    const raw = localStorage.getItem(LOCKOUT_KEY);
    if (raw) {
      const s = JSON.parse(raw) as LockoutState;
      if (typeof s.fails === 'number' && typeof s.until === 'number') return s;
    }
  } catch { /* Best-effort operation; failure is non-fatal. */ }
  return { fails: 0, until: 0 };
}

function saveLockoutState(s: LockoutState): void {
  try {
    localStorage.setItem(LOCKOUT_KEY, JSON.stringify(s));
  } catch { /* Best-effort operation; failure is non-fatal. */ }
}

/** 距解锁锁定解除还剩多少毫秒（0 = 未锁定） */
export function getUnlockLockoutRemainingMs(): number {
  const s = getLockoutState();
  return Math.max(0, s.until - Date.now());
}

function validatePassphrase(passphrase: string): void {
  if (passphrase.length < MIN_PASS_LENGTH) {
    throw new Error(`口令至少 ${MIN_PASS_LENGTH} 位（建议字母+数字混合）`);
  }
}

// 校验口令并缓存密钥（成功返回 true；锁定期间抛错）
export async function unlockEncryption(passphrase: string): Promise<boolean> {
  const settings = getEncSettings();
  if (!settings) return false;
  const lock = getLockoutState();
  if (lock.until > Date.now()) {
    const mins = Math.ceil((lock.until - Date.now()) / 60_000);
    throw new Error(`口令尝试次数过多，已锁定，请 ${mins} 分钟后再试`);
  }
  const iterations = settings.iterations || LEGACY_ITERATIONS;
  const bits = await deriveBits(passphrase, base64ToBuf(settings.salt), iterations);
  if (bufToHex(bits) !== settings.verifier) {
    const fails = lock.fails + 1;
    saveLockoutState(
      fails >= MAX_UNLOCK_FAILS ? { fails: 0, until: Date.now() + LOCKOUT_MS } : { fails, until: 0 }
    );
    return false;
  }
  saveLockoutState({ fails: 0, until: 0 });
  cachedKey = await importAesKey(bits);
  try {
    sessionStorage.setItem(SESSION_KEY, bufToBase64(bits));
  } catch { /* Best-effort operation; failure is non-fatal. */ }
  // 旧迭代次数（100k）自动升级至当前标准（600k）：后台重新派生密钥并重加密全部数据，失败下次解锁重试
  if (iterations < ITERATIONS) {
    void (async () => {
      try {
        await requireStorageMigrator().reencryptAll(passphrase);
        console.info('加密强度已自动升级（PBKDF2 600k 迭代）');
      } catch (e) {
        console.warn('加密强度自动升级失败，将在下次解锁时重试', e);
      }
    })();
  }
  return true;
}

export async function getEncryptionKey(): Promise<CryptoKey | null> {
  if (cachedKey) return cachedKey;
  const settings = getEncSettings();
  if (!settings) return null;
  try {
    const sessionBits = base64ToBuf(sessionStorage.getItem(SESSION_KEY) || '');
    if (sessionBits.length === 32) {
      const verifier = bufToHex(sessionBits);
      if (verifier === settings.verifier) {
        cachedKey = await importAesKey(sessionBits.buffer as ArrayBuffer);
        return cachedKey;
      }
    }
  } catch { /* Best-effort operation; failure is non-fatal. */ }
  return null; // 未解锁
}

// ===== 启用 / 修改 / 关闭 =====
/** 以当前标准（新 salt + 600k 迭代）重设密钥；不校验口令强度（供 reencryptAll / 迭代升级复用） */
export async function rekeyEncryption(passphrase: string): Promise<void> {
  const prev = getEncSettings();
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const bits = await deriveBits(passphrase, salt, ITERATIONS);
  cachedKey = await importAesKey(bits);
  const settings: EncSettings = {
    enabled: true,
    salt: bufToBase64(salt),
    verifier: bufToHex(bits),
    iterations: ITERATIONS,
    createdAt: prev?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  try {
    sessionStorage.setItem(SESSION_KEY, bufToBase64(bits));
  } catch { /* Best-effort operation; failure is non-fatal. */ }
}

export async function enableEncryption(passphrase: string): Promise<void> {
  validatePassphrase(passphrase);
  await rekeyEncryption(passphrase);
}

export async function changeEncryptionPassphrase(oldPass: string, newPass: string): Promise<boolean> {
  if (!(await unlockEncryption(oldPass))) return false;
  validatePassphrase(newPass);
  // 用旧 key 解密全部敏感数据 → 用新口令重新加密
  await requireStorageMigrator().reencryptAll(newPass);
  return true;
}

export async function disableEncryption(passphrase: string): Promise<boolean> {
  if (!(await unlockEncryption(passphrase))) return false;
  await requireStorageMigrator().decryptAllToPlain();
  cachedKey = null;
  localStorage.removeItem(SETTINGS_KEY);
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch { /* Best-effort operation; failure is non-fatal. */ }
  return true;
}

// ===== 加解密负载 =====
export async function encryptPayload(plainText: string, key: CryptoKey): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder();
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv }, key, enc.encode(plainText));
  return `${ENC_PREFIX}${bufToBase64(iv)}:${bufToBase64(ct)}`;
}

export function isEncryptedPayload(v: unknown): boolean {
  return typeof v === 'string' && v.startsWith(ENC_PREFIX);
}

export async function decryptPayload(payload: string, key: CryptoKey): Promise<string> {
  const body = payload.slice(ENC_PREFIX.length);
  const sep = body.indexOf(':');
  const iv = base64ToBuf(body.slice(0, sep));
  const ct = base64ToBuf(body.slice(sep + 1));
  const plain = await subtle().decrypt({ name: 'AES-GCM', iv: iv.buffer as ArrayBuffer }, key, ct.buffer as ArrayBuffer);
  return new TextDecoder().decode(plain);
}
